/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState } from "react";
import PropTypes from "prop-types";
import { INVOICING_RULE_CATEGORIES, JURISDICTION_FEE_TYPES } from "@constants";
import utils from "@utils";
import { ReactSelect, Tooltip } from "../../../../components";
import "./InvoicingRulesModal.scss";

const RatesRules = props => {
	const { lang } = global;
	const {
		invoiceRules,
		clients,
		ranks,
		paycodes,
		userData,
		onCreate,
		onUpdate,
		onDelete,
		onRuleBulkUpdate,
		onBulkCreate,
		baseRatesRules,
		onSetInvoicingRule,
	} = props;

	const [newRule, setNewRule] = useState(null);
	const [holidays, setHolidays] = useState([]);
	const [holidayDateInput, setHolidayDateInput] = useState("");
	const [showDatePicker, setShowDatePicker] = useState(false);

	const parseDate = dateString => {
		const dateComponents = dateString.split("-");
		return `${dateComponents[1]}/${dateComponents[2]}/${dateComponents[0]}`;
	};

	const formatDate = groupedRuleKey => {
		const rule = invoiceRules[groupedRuleKey].rules[0];
		const effectiveDateStart = parseDate(rule.effectiveDateStart.split("T")[0]);
		const effectiveDateEnd = parseDate(rule.effectiveDateEnd.split("T")[0]);
		if (baseRatesRules) {
			return `${effectiveDateStart} - ${effectiveDateEnd}`;
		}
		return effectiveDateStart;
	};

	const addOneDay = dateStr => {
		const [year, month, day] = dateStr.split("-").map(Number);
		const date = new Date(year, month - 1, day);
		date.setDate(date.getDate() + 1);
		const newYear = date.getFullYear();
		const newMonth = `0${date.getMonth() + 1}`.slice(-2);
		const newDay = `0${date.getDate()}`.slice(-2);
		return `${newYear}-${newMonth}-${newDay}`;
	};

	const validatePositiveNumericValue = val => {
		if (val === "") return true;
		if (!val.match(/^\d*\.?\d{0,2}$/g)) {
			return false;
		}
		if (parseFloat(val) < 0) {
			return false;
		}
		return true;
	};

	const validatePercentageValue = rule => {
		if (rule.fee === "" || rule.fee == null) return true;
		if ((rule.feeType === 3 || rule.feeType === "3") && (rule.fee <= 0 || rule.fee > 100)) {
			return false;
		}
		return true;
	};

	const getRuleCriterias = data => {
		const criterias = [];

		data.forEach(rule => {
			const { jobPaycodeId, clientId, officerRankId } = rule;
			const criteria = { jobPaycodeId, clientId, officerRankId };

			if (
				!criterias.some(
					c =>
						c.jobPaycodeId === jobPaycodeId &&
						c.clientId === clientId &&
						c.officerRankId === officerRankId,
				)
			) {
				criterias.push(criteria);
			}
		});

		return criterias;
	};

	const checkIfRuleCriteriaExists = (array, rule) => {
		for (let i = 0; i < array.length; i++) {
			const existingRule = array[i];
			if (
				existingRule.jobPaycodeId === rule.jobPaycodeId &&
				existingRule.clientId === rule.clientId &&
				existingRule.officerRankId === rule.officerRankId
			) {
				return true;
			}
		}
		return false;
	};

	const validDateRange = (dateRanges, rule) => {
		const start = new Date(rule.effectiveDateStart);
		const end = new Date(rule.effectiveDateEnd);

		return (
			!dateRanges
				.filter(r => {
					const [rangeStart, rangeEnd] = r.split(" - ").map(dateStr => new Date(dateStr));

					return (
						(start >= rangeStart && start < rangeEnd) ||
						(end > rangeStart && end < rangeEnd) ||
						(start <= rangeStart && end > rangeEnd)
					);
				})
				.filter(r => {
					const rulesToConsider = invoiceRules[r].rules.filter(ru => ru.id !== rule.id);
					const criterias = getRuleCriterias(rulesToConsider);
					return checkIfRuleCriteriaExists(criterias, rule);
				}).length > 0
		);
	};

	const handleRuleChange = (e, ruleId, groupKey) => {
		const groupedRulesAux = { ...invoiceRules };
		const rule = groupedRulesAux[groupKey].rules.find(r => r.id === ruleId);

		let isRateValid = true;
		if (e.name === "officerRate" || e.name === "fee") {
			isRateValid = validatePositiveNumericValue(e.value);
		}
		if (isRateValid) {
			rule[e.name] = e.value;
			onSetInvoicingRule(groupedRulesAux);
		}
	};

	const deleteRule = (ruleId, groupKey) => {
		const deleteCallback = () => {
			const groupedRulesAux = { ...invoiceRules };
			const aux = groupedRulesAux[groupKey].rules.filter(rule => rule.id !== ruleId);
			groupedRulesAux[groupKey].rules = aux;

			if (groupedRulesAux[groupKey].rules.length <= 0) {
				delete groupedRulesAux[groupKey];
			}

			onSetInvoicingRule(groupedRulesAux);
		};

		onDelete(ruleId, deleteCallback);
	};

	const updateRule = (ruleId, groupedRuleKey) => {
		const callback = () => {
			const groupedRulesAux = { ...invoiceRules };
			const rule = groupedRulesAux[groupedRuleKey].rules.find(r => r.id === ruleId);
			if (rule) rule._editing = !rule._editing;
			onSetInvoicingRule(groupedRulesAux);
		};

		const rule = invoiceRules[groupedRuleKey].rules.find(r => r.id === ruleId);
		const rulesToConsider = invoiceRules[groupedRuleKey].rules.filter(r => r.id !== ruleId);
		const criterias = getRuleCriterias(rulesToConsider);

		if (checkIfRuleCriteriaExists(criterias, rule)) {
			utils.notify({
				type: "error",
				message: lang.t(
					"Unable to update rule An existing rule for the same date range has the same criteria",
				),
			});
			return;
		}

		if (!validatePercentageValue(rule)) {
			utils.notify({
				type: "error",
				message: lang.t("Percentage based fees must be between 0 and 100"),
			});
			return;
		}

		onUpdate(
			{
				Id: rule.id,
				ClientId: rule.clientId === -1 ? null : rule.clientId,
				OrganizationId: userData.Organization.Id,
				OfficerRankId: rule.officerRankId === -1 ? null : rule.officerRankId,
				OfficerRate: rule.officerRate,
				JobPaycodeId: rule.jobPaycodeId === -1 ? null : rule.jobPaycodeId,
				SetPaycode: rule.paycodeId,
				AgencyFee: rule.fee,
				FeeType: rule.feeType,
				EffectiveDateStart: rule.effectiveDateStart,
				EffectiveDateEnd: rule.effectiveDateEnd,
				InvoicingRuleCategoryId: baseRatesRules
					? INVOICING_RULE_CATEGORIES.BASERATES
					: INVOICING_RULE_CATEGORIES.HOLIDAYRATES,
			},
			callback,
		);
	};

	const validateRate = rate => {
		if (!baseRatesRules && holidays.length === 0) return false;

		if (
			rate.officerRate == null ||
			rate.officerRate === "" ||
			(baseRatesRules &&
				(rate.effectiveDateStart == null || rate.effectiveDateStart === "")) ||
			(baseRatesRules && (rate.effectiveDateEnd == null || rate.effectiveDateEnd === ""))
		) {
			return false;
		}

		if (
			rate._showFee &&
			(rate.fee == null || rate.fee === "" || rate.feeType == null || rate.feeType === "")
		)
			return false;

		if (new Date(rate.effectiveDateEnd) <= new Date(rate.effectiveDateStart)) return false;

		return true;
	};

	const validateUpdatingRule = (groupedKey, rule) => {
		if (rule.officerRate == null || rule.officerRate === "") return false;

		if (
			rule._showFee &&
			(rule.fee == null || rule.fee === "" || rule.feeType == null || rule.feeType === "")
		)
			return false;

		return true;
	};

	const validateGroupedRulesUpdate = (rules, rangesToConsider) => {
		let valid = true;
		rules.forEach(r => {
			const validRule = validDateRange(rangesToConsider, r);
			if (!validRule) valid = false;
		});
		return valid;
	};

	const bulkUpdate = groupedRuleKey => {
		const bulkUpdateCallback = () => {
			const groupedRulesAux = { ...invoiceRules };
			delete groupedRulesAux[groupedRuleKey];
			onSetInvoicingRule(groupedRulesAux);
		};

		const ids = invoiceRules[groupedRuleKey].rules.map(rule => rule.id);
		const { effectiveDateStart, effectiveDateEnd } = invoiceRules[groupedRuleKey].rules[0];

		const rangesToConsider = Object.keys(invoiceRules).filter(key => key !== groupedRuleKey);
		const valid = validateGroupedRulesUpdate(
			invoiceRules[groupedRuleKey].rules,
			rangesToConsider,
		);
		if (!valid) {
			utils.notify({
				type: "error",
				message: lang.t("The date range provided overlaps with an existing one"),
			});
			return;
		}

		onRuleBulkUpdate(
			ids,
			{
				effectiveDateStart,
				effectiveDateEnd,
			},
			bulkUpdateCallback,
		);
	};

	const handleGroupedDateRangeChange = (e, groupedKey) => {
		const groupedRulesAux = { ...invoiceRules };
		const groupedAux = groupedRulesAux[groupedKey].rules.map(rule => {
			const ruleAux = { ...rule };
			ruleAux[e.name] = e.value;
			return ruleAux;
		});

		groupedRulesAux[groupedKey].rules = groupedAux;
		onSetInvoicingRule(groupedRulesAux);
	};

	const handleHolidayGroupedDateRangeChange = (e, groupedKey) => {
		const groupedRulesAux = { ...invoiceRules };
		const groupedAux = groupedRulesAux[groupedKey].rules.map(rule => {
			const ruleAux = { ...rule };
			ruleAux.effectiveDateStart = e.value;
			ruleAux.effectiveDateEnd = addOneDay(e.value);
			return ruleAux;
		});

		groupedRulesAux[groupedKey].rules = groupedAux;
		onSetInvoicingRule(groupedRulesAux);
	};

	const validateGroupedDateRangeChange = groupKey => {
		const rule = invoiceRules[groupKey].rules[0];

		if (new Date(rule.effectiveDateEnd) <= new Date(rule.effectiveDateStart)) return false;

		return true;
	};

	const toggleCriteriaEdit = groupedKey => {
		const groupedRulesAux = { ...invoiceRules };
		groupedRulesAux[groupedKey]._editing = !groupedRulesAux[groupedKey]._editing;
		const rulesAux = groupedRulesAux[groupedKey].rules.map(rule => {
			const ruleAux = { ...rule };
			ruleAux.originalState = { ...rule };
			return ruleAux;
		});
		groupedRulesAux[groupedKey].rules = rulesAux;
		onSetInvoicingRule(groupedRulesAux);
	};

	const cancelToggleCriteriaEdit = groupedKey => {
		const groupedRulesAux = { ...invoiceRules };
		groupedRulesAux[groupedKey]._editing = !groupedRulesAux[groupedKey]._editing;
		const rulesAux = groupedRulesAux[groupedKey].rules.map(rule => rule.originalState);
		groupedRulesAux[groupedKey].rules = rulesAux;
		onSetInvoicingRule(groupedRulesAux);
	};

	const handleRuleGroupExpand = groupedKey => {
		const groupedRulesAux = { ...invoiceRules };
		if (!groupedRulesAux[groupedKey]._collapsed) groupedRulesAux[groupedKey]._newRule = {};
		groupedRulesAux[groupedKey]._collapsed = !groupedRulesAux[groupedKey]._collapsed;
		onSetInvoicingRule(groupedRulesAux);
	};

	const toggleEdit = (id, groupedKey) => {
		const groupedRulesAux = { ...invoiceRules };
		const aux = groupedRulesAux[groupedKey].rules.map(rule => {
			const ruleAux = { ...rule };

			if (rule.id === id) {
				ruleAux._editing = !ruleAux._editing;
				ruleAux.originalState = { ...rule };
			}

			return ruleAux;
		});

		groupedRulesAux[groupedKey].rules = aux;
		onSetInvoicingRule(groupedRulesAux);
	};

	const duplicateRule = (id, groupedKey) => {
		const groupedRulesAux = { ...invoiceRules };
		const rule = groupedRulesAux[groupedKey].rules.find(r => r.id === id);
		const duplicatedRule = {
			jobPaycodeId: rule.jobPaycodeId,
			clientId: rule.clientId,
			officerRankId: rule.officerRankId,
			officerRate: rule.officerRate,
			paycodeId: rule.paycodeId,
			fee: rule.fee,
			feeType: rule.feeType,
			effectiveDateStart: rule.effectiveDateStart,
			effectiveDateEnd: rule.effectiveDateEnd,
			invoicingRuleCategoryId: rule.invoicingRuleCategoryId,
			_showFee: !!rule.fee,
		};

		groupedRulesAux[groupedKey]._newRule = duplicatedRule;
		onSetInvoicingRule(groupedRulesAux);
	};

	const cancelRuleToggleEdit = (id, groupedKey) => {
		const groupedRulesAux = { ...invoiceRules };
		const aux = groupedRulesAux[groupedKey].rules.map(rule => {
			let ruleAux = { ...rule };

			if (rule.id === id) {
				ruleAux = ruleAux.originalState;
			}

			return ruleAux;
		});
		groupedRulesAux[groupedKey].rules = aux;
		onSetInvoicingRule(groupedRulesAux);
	};

	const addFeeToRule = (ruleId, groupKey) => {
		const groupedRulesAux = { ...invoiceRules };
		const rule = groupedRulesAux[groupKey].rules.find(r => r.id === ruleId);

		rule.fee = "";
		rule.feeType = 1;

		onSetInvoicingRule(groupedRulesAux);
	};

	const findJobPayCodeName = id => {
		const jobPayCode = paycodes.find(paycode => paycode.value === id);
		if (jobPayCode) return jobPayCode.label;
		return "";
	};

	const findOfficerRankName = id => {
		const rank = ranks.find(r => r.value === id);
		if (rank) return rank.label;
		return "";
	};

	const findClientName = id => {
		const client = clients.find(c => c.value === id);
		if (client) return client.label;
		return "";
	};

	const findFeeTypeName = id => {
		const fee = JURISDICTION_FEE_TYPES.find(f => f.value === id);
		if (fee) return fee.label;
		return "";
	};

	const addNewRule = () => {
		setNewRule({
			jobPaycodeId: -1,
			clientId: -1,
			officerRankId: -1,
			officerRate: null,
			paycodeId: null,
			fee: "",
			feeType: 1,
			effectiveDateStart: "",
			effectiveDateEnd: "",
			invoicingRuleCategoryId: baseRatesRules
				? INVOICING_RULE_CATEGORIES.BASERATES
				: INVOICING_RULE_CATEGORIES.HOLIDAYRATES,
			_showFee: false,
		});
	};

	const addFeeToNewRule = () => {
		const ruleAux = { ...newRule };
		ruleAux._showFee = true;
		setNewRule(ruleAux);
	};

	const updateNewRule = e => {
		let isRateValid = true;
		const aux = { ...newRule };
		if (e.name === "officerRate" || e.name === "fee") {
			isRateValid = validatePositiveNumericValue(e.value);
		}
		if (isRateValid) {
			aux[e.name] = e.value;
			setNewRule(aux);
		}
	};

	const validMultipleDateRange = (dateRanges, rule, newHolidayDatesRanges) => {
		let validDates = true;
		newHolidayDatesRanges.forEach(range => {
			const auxNewRule = { ...rule };
			auxNewRule.effectiveDateStart = range.EffectiveDateStart;
			auxNewRule.effectiveDateEnd = range.EffectiveDateEnd;

			const valid = validDateRange(dateRanges, auxNewRule);
			if (!valid) {
				validDates = false;
				utils.notify({
					type: "error",
					message: lang.t(
						`The rule for: ${parseDate(range.EffectiveDateStart)} overlaps with an existing rule. Please modify the date or criteria`,
					),
				});
			}
		});
		return validDates;
	};

	const createHolidayRule = () => {
		const bulkCreateCallback = () => {
			setNewRule(null);
			setHolidays([]);
		};

		const newHolidayDatesRanges = holidays.map(holiday => ({
			EffectiveDateStart: holiday,
			EffectiveDateEnd: addOneDay(holiday),
		}));

		const existingDateRanges = Object.keys(invoiceRules);
		if (!validMultipleDateRange(existingDateRanges, newRule, newHolidayDatesRanges)) return;

		if (!validatePercentageValue(newRule)) {
			utils.notify({
				type: "error",
				message: lang.t("Percentage based fees must be between 0 and 100"),
			});
			return;
		}

		onBulkCreate(
			{
				DateRanges: newHolidayDatesRanges,
				ClientId: newRule.clientId === -1 ? null : newRule.clientId,
				OrganizationId: userData.Organization.Id,
				OfficerRankId: newRule.officerRankId === -1 ? null : newRule.officerRankId,
				OfficerRate: newRule.officerRate,
				JobPaycodeId: newRule.jobPaycodeId === -1 ? null : newRule.jobPaycodeId,
				SetPaycode: newRule.paycodeId,
				AgencyFee: newRule.fee,
				FeeType: newRule.feeType,
				InvoicingRuleCategoryId: INVOICING_RULE_CATEGORIES.HOLIDAYRATES,
			},
			bulkCreateCallback,
		);
	};

	const createNewRule = () => {
		const createCallback = () => {
			setNewRule(null);
		};

		if (!baseRatesRules) {
			createHolidayRule();
			return;
		}

		const existingDateRanges = Object.keys(invoiceRules);
		const valid = validDateRange(existingDateRanges, newRule);
		if (!validatePercentageValue(newRule)) {
			utils.notify({
				type: "error",
				message: lang.t("Percentage based fees must be between 0 and 100"),
			});
			return;
		}

		if (!valid) {
			utils.notify({
				type: "error",
				message: lang.t("The new invoicing rule date range overlaps with an existing one"),
			});
			return;
		}

		onCreate(
			{
				ClientId: newRule.clientId === -1 ? null : newRule.clientId,
				OrganizationId: userData.Organization.Id,
				OfficerRankId: newRule.officerRankId === -1 ? null : newRule.officerRankId,
				OfficerRate: newRule.officerRate,
				JobPaycodeId: newRule.jobPaycodeId === -1 ? null : newRule.jobPaycodeId,
				SetPaycode: newRule.paycodeId,
				AgencyFee: newRule.fee,
				FeeType: newRule.feeType,
				EffectiveDateStart: newRule.effectiveDateStart,
				EffectiveDateEnd: newRule.effectiveDateEnd,
				InvoicingRuleCategoryId: baseRatesRules
					? INVOICING_RULE_CATEGORIES.BASERATES
					: INVOICING_RULE_CATEGORIES.HOLIDAYRATES,
			},
			createCallback,
		);
	};

	const addNewRuleToDateRange = groupedKey => {
		const groupedRulesAux = { ...invoiceRules };
		const { effectiveDateStart, effectiveDateEnd } = groupedRulesAux[groupedKey].rules[0];

		groupedRulesAux[groupedKey]._collapsed = false;
		groupedRulesAux[groupedKey]._newRule = {
			jobPaycodeId: -1,
			clientId: -1,
			officerRankId: -1,
			officerRate: null,
			paycodeId: null,
			fee: "",
			feeType: 1,
			invoicingRuleCategoryId: baseRatesRules
				? INVOICING_RULE_CATEGORIES.BASERATES
				: INVOICING_RULE_CATEGORIES.HOLIDAYRATES,
			_showFee: false,
			effectiveDateStart,
			effectiveDateEnd,
		};

		onSetInvoicingRule(groupedRulesAux);
	};

	const addFeeToNewRuleFromDateRange = groupedKey => {
		const groupedRulesAux = { ...invoiceRules };
		groupedRulesAux[groupedKey]._newRule._showFee = true;
		onSetInvoicingRule(groupedRulesAux);
	};

	const cancelNewRuleFromDateRange = groupedKey => {
		const groupedRulesAux = { ...invoiceRules };
		groupedRulesAux[groupedKey]._newRule = {};
		onSetInvoicingRule(groupedRulesAux);
	};

	const updateNewRuleFromDateRange = (e, groupedKey) => {
		let isRateValid = true;
		const groupedRulesAux = { ...invoiceRules };
		const rule = groupedRulesAux[groupedKey]._newRule;
		if (e.name === "officerRate" || e.name === "fee") {
			isRateValid = validatePositiveNumericValue(e.value);
		}
		if (isRateValid) {
			rule[e.name] = e.value;
			onSetInvoicingRule(groupedRulesAux);
		}
	};

	const saveNewRuleFromDateRange = groupedKey => {
		const createCallback = () => {
			const groupedRulesAux = { ...invoiceRules };
			groupedRulesAux[groupedKey]._newRule = {};
			onSetInvoicingRule(groupedRulesAux);
		};

		const rule = invoiceRules[groupedKey]._newRule;

		const rulesToConsider = invoiceRules[groupedKey].rules;
		const criterias = getRuleCriterias(rulesToConsider);

		if (checkIfRuleCriteriaExists(criterias, rule)) {
			utils.notify({
				type: "error",
				message: lang.t(
					"Unable to create rule The new rule has the same criteria as an existing one",
				),
			});
			return;
		}

		if (!validatePercentageValue(rule)) {
			utils.notify({
				type: "error",
				message: lang.t("Percentage based fees must be between 0 and 100"),
			});
			return;
		}

		onCreate(
			{
				ClientId: rule.clientId === -1 ? null : rule.clientId,
				OrganizationId: userData.Organization.Id,
				OfficerRankId: rule.officerRankId === -1 ? null : rule.officerRankId,
				OfficerRate: rule.officerRate,
				JobPaycodeId: rule.jobPaycodeId === -1 ? null : rule.jobPaycodeId,
				SetPaycode: rule.paycodeId,
				AgencyFee: rule.fee,
				FeeType: rule.feeType,
				EffectiveDateStart: rule.effectiveDateStart,
				EffectiveDateEnd: rule.effectiveDateEnd,
				InvoicingRuleCategoryId: baseRatesRules
					? INVOICING_RULE_CATEGORIES.BASERATES
					: INVOICING_RULE_CATEGORIES.HOLIDAYRATES,
			},
			createCallback,
		);
	};

	const handleAddHolidayRate = () => {
		setShowDatePicker(!showDatePicker);
	};

	const cancelAddHolidayRateInput = () => {
		setShowDatePicker(false);
		setHolidayDateInput("");
	};

	const handleAddCustomDate = () => {
		if (holidayDateInput == null || holidayDateInput === "") return;
		if (holidays.some(item => item === holidayDateInput)) {
			utils.notify({
				type: "error",
				message: lang.t("Already selected holiday rate date"),
			});
			return;
		}

		const auxHolidays = [...holidays, holidayDateInput];
		setHolidayDateInput("");
		setHolidays(auxHolidays);
		setShowDatePicker(!showDatePicker);
	};

	const deleteCustomDate = date => {
		let auxHolidays = [...holidays];
		auxHolidays = auxHolidays.filter(i => i !== date);
		setHolidays(auxHolidays);
	};

	const cancelNewRule = () => {
		setNewRule(null);
		if (!baseRatesRules) {
			setHolidays([]);
		}
	};

	return (
		<div>
			<div className="row-title">
				<Choose>
					<When condition={baseRatesRules}>
						<h5>{lang.t("Base Rates")} </h5>
						<Tooltip
							className="tool-tip"
							content={lang.t("Base Rate rules Tooltip")}
						>
							<i className="hidden-xs rkf rkf-question-solid tooltip-icon" />
						</Tooltip>
					</When>
					<Otherwise>
						<h5>{lang.t("Holiday Rates")}</h5>
						<Tooltip
							className="tool-tip"
							content={lang.t("Holiday Rates Tooltip")}
						>
							<i className="hidden-xs rkf rkf-question-solid tooltip-icon" />
						</Tooltip>
					</Otherwise>
				</Choose>
			</div>
			<If condition={Object.keys(invoiceRules).length === 0}>
				<div className="empty-row-text">{lang.t("No rules found")}</div>
			</If>
			<If condition={Object.keys(invoiceRules).length !== 0}>
				<For
					each="groupedRuleKey"
					of={Object.keys(invoiceRules)}
					index="index"
				>
					<div
						className="grouped-rule-section"
						key={`grouped-rule-section-${groupedRuleKey}`}
					>
						<If condition={!invoiceRules[groupedRuleKey]._editing}>
							<If condition={!invoiceRules[groupedRuleKey]._collapsed}>
								<span style={{ color: "#99a0a7" }}>
									{baseRatesRules
										? lang.t("Rule Date Range")
										: lang.t("Rule Date")}
								</span>
							</If>
							<div
								className="row"
								key={`rule-${groupedRuleKey}`}
							>
								<div className="col-xs-12 col-md-4">
									<div className="row">
										<div
											style={{ display: "flex", alignItems: "center" }}
											className="col-xs-10 col-md-12"
										>
											<strong>{formatDate(groupedRuleKey)}</strong>
											<div
												className="hidden-xs hidden-sm"
												onClick={() =>
													handleRuleGroupExpand(groupedRuleKey)
												}
												onKeyPress={({ key }) => {
													if (key === "Enter")
														handleRuleGroupExpand(groupedRuleKey);
												}}
											>
												<i
													style={{ paddingLeft: 10, cursor: "pointer" }}
													className={`h4 rkf ${!invoiceRules[groupedRuleKey]._collapsed ? "rkf-chevron-up" : "rkf-chevron-down"}`}
												/>
											</div>
											<button
												style={{ paddingLeft: 10 }}
												type="button"
												className="btn btn-link"
												onClick={() => toggleCriteriaEdit(groupedRuleKey)}
											>
												<i className="glyphicon glyphicon-pencil" />
											</button>
										</div>
										<div
											style={{ display: "flex", alignItems: "center" }}
											className="col-xs-1 visible-xs-* hidden-md hidden-lg"
											onClick={() => handleRuleGroupExpand(groupedRuleKey)}
											onKeyPress={({ key }) => {
												if (key === "Enter")
													handleRuleGroupExpand(groupedRuleKey);
											}}
										>
											<i
												style={{ cursor: "pointer" }}
												className={`h4 rkf ${!invoiceRules[groupedRuleKey]._collapsed ? "rkf-chevron-up" : "rkf-chevron-down"}`}
											/>
										</div>
									</div>
								</div>
								<If
									condition={
										Object.keys(invoiceRules[groupedRuleKey]._newRule)
											.length === 0
									}
								>
									<div className="col-xs-12 col-md-4 col-md-offset-4">
										<button
											type="button"
											style={{ paddingLeft: 0 }}
											className="btn btn-link"
											title={lang.t("Add base rate")}
											onClick={() => addNewRuleToDateRange(groupedRuleKey)}
										>
											<span>
												+ {lang.t("Add new rule with this date range")}
											</span>
										</button>
									</div>
								</If>
							</div>
						</If>
						<If condition={invoiceRules[groupedRuleKey]._editing}>
							<div className="row">
								<Choose>
									<When condition={baseRatesRules}>
										<div className="col-xs-5 col-md-3">
											{lang.t("Start Date")}*
										</div>
										<div className="col-xs-5 col-md-3">
											{lang.t("End Date")}*
										</div>
									</When>
									<Otherwise>
										<div className="col-xs-6 col-md-3">{lang.t("Date")}*</div>
									</Otherwise>
								</Choose>
							</div>
							<div
								className="row"
								key={`rule-${groupedRuleKey}`}
							>
								<Choose>
									<When condition={baseRatesRules}>
										<div className="col-xs-5 col-md-3 form-group">
											<input
												name="effectiveDateStart"
												id={`effectiveDateStart-${groupedRuleKey}`}
												className="form-control"
												type="date"
												value={
													invoiceRules[groupedRuleKey].rules[0]
														.effectiveDateStart
												}
												onChange={e =>
													handleGroupedDateRangeChange(
														e.target,
														groupedRuleKey,
													)
												}
											/>
										</div>
										<div className="col-xs-5 col-md-3 form-group">
											<input
												name="effectiveDateEnd"
												id={`effectiveDateEnd-${groupedRuleKey}`}
												className="form-control"
												type="date"
												min={
													invoiceRules[groupedRuleKey].rules[0]
														.effectiveDateStart
												}
												value={
													invoiceRules[groupedRuleKey].rules[0]
														.effectiveDateEnd
												}
												onChange={e =>
													handleGroupedDateRangeChange(
														e.target,
														groupedRuleKey,
													)
												}
											/>
										</div>
									</When>
									<Otherwise>
										<div className="col-xs-6 col-md-3 form-group">
											<input
												name="effectiveDateStart"
												id={`effectiveDateStart-${groupedRuleKey}`}
												className="form-control"
												type="date"
												value={
													invoiceRules[groupedRuleKey].rules[0]
														.effectiveDateStart
												}
												onChange={e =>
													handleHolidayGroupedDateRangeChange(
														e.target,
														groupedRuleKey,
													)
												}
											/>
										</div>
									</Otherwise>
								</Choose>
								<div
									className={`col-xs-2 visible-xs-* hidden-md hidden-lg ${baseRatesRules ? "" : "col-xs-offset-4 col-sm-offset-4"}`}
									onClick={() => handleRuleGroupExpand(groupedRuleKey)}
									onKeyPress={({ key }) => {
										if (key === "Enter") handleRuleGroupExpand(groupedRuleKey);
									}}
								>
									<i
										className={`h4 rkf ${!invoiceRules[groupedRuleKey]._collapsed ? "rkf-chevron-up" : "rkf-chevron-down"}`}
									/>
								</div>
								<div className="col-xs-7 col-md-2">
									<div className="edit-rule-buttons">
										<button
											id={`cancel-new-rule-${invoiceRules[groupedRuleKey]}`}
											className="btn danger"
											type="button"
											onClick={() => cancelToggleCriteriaEdit(groupedRuleKey)}
										>
											{lang.t("Cancel")}
										</button>
										<button
											id={`save-new-rule-${invoiceRules[groupedRuleKey]}`}
											className="btn primary"
											type="button"
											disabled={
												!validateGroupedDateRangeChange(groupedRuleKey)
											}
											onClick={() => bulkUpdate(groupedRuleKey)}
										>
											{lang.t("Save")}
										</button>
										<div
											className="hidden-xs hidden-sm"
											onClick={() => handleRuleGroupExpand(groupedRuleKey)}
											onKeyPress={({ key }) => {
												if (key === "Enter")
													handleRuleGroupExpand(groupedRuleKey);
											}}
										>
											<i
												className={`h4 rkf ${!invoiceRules[groupedRuleKey]._collapsed ? "rkf-chevron-up" : "rkf-chevron-down"}`}
											/>
										</div>
									</div>
								</div>
							</div>
						</If>
						<If condition={!invoiceRules[groupedRuleKey]._collapsed}>
							{/* Rules Header */}
							<div className="row rule-list-headers text-left hidden-xs hidden-sm">
								<div className="col-xs-10">
									<div className="row">
										<div className="col-xs-2">{lang.t("Pay Code")}</div>
										<div className="col-xs-2">{lang.t("Officer Rank")}</div>
										<div className="col-xs-2">{lang.t("Client")}</div>
										<div className="col-xs-2 text-right">{lang.t("Rate")}</div>
										<div className="col-xs-2">{lang.t("Set Paycode")}</div>
										<div className="col-xs-2">{lang.t("Fee")}</div>
									</div>
								</div>
							</div>
							<For
								each="rule"
								of={invoiceRules[groupedRuleKey].rules}
							>
								<If condition={!rule._editing}>
									<div
										className="row row-header"
										key={`rule-${rule.id}`}
									>
										<div style={{ display: "flex", alignItems: "center" }}>
											<div className="col-xs-12 col-md-10">
												<div className="row">
													<div className="col-md-2 invoice-form-padding">
														<span className="hidden-xs hidden-sm">
															{findJobPayCodeName(rule.jobPaycodeId)}
														</span>
														<div className="row visible-xs-* hidden-md hidden-lg">
															<div className="col-xs-9">
																<span className="">
																	<strong>
																		{lang.t("Pay Code")}:{" "}
																	</strong>
																</span>
																<span>
																	{findJobPayCodeName(
																		rule.jobPaycodeId,
																	)}
																</span>
															</div>
															<div className="col-xs-3">
																<div className="row">
																	<div className="col-xs-3">
																		<button
																			style={{ padding: 0 }}
																			type="button"
																			className="btn btn-link"
																			onClick={() =>
																				duplicateRule(
																					rule.id,
																					groupedRuleKey,
																				)
																			}
																		>
																			<i className="glyphicon glyphicon-duplicate" />
																		</button>
																	</div>
																	<div className="col-xs-3">
																		<button
																			style={{ padding: 0 }}
																			type="button"
																			className="btn btn-link"
																			onClick={() =>
																				toggleEdit(
																					rule.id,
																					groupedRuleKey,
																				)
																			}
																		>
																			<i className="glyphicon glyphicon-pencil" />
																		</button>
																	</div>
																	<div className="col-xs-3">
																		<button
																			style={{ padding: 0 }}
																			type="button"
																			className="btn btn-link"
																			onClick={() =>
																				deleteRule(
																					rule.id,
																					groupedRuleKey,
																				)
																			}
																		>
																			<i className="glyphicon glyphicon-trash text-danger" />
																		</button>
																	</div>
																</div>
															</div>
														</div>
													</div>
													<div className="col-md-2 invoice-form-padding">
														<span className="visible-xs-* hidden-md hidden-lg">
															<strong>
																{lang.t("Officer Rank")}:{" "}
															</strong>
														</span>
														<span>
															{findOfficerRankName(
																rule.officerRankId,
															)}
														</span>
													</div>
													<div className="col-md-2 invoice-form-padding">
														<span className="visible-xs-* hidden-md hidden-lg">
															<strong>{lang.t("Client")}: </strong>
														</span>
														<span>{findClientName(rule.clientId)}</span>
													</div>
													<div className="col-md-2 invoice-form-padding rate-align">
														<span className="visible-xs-* hidden-md hidden-lg">
															<strong>{lang.t("Rate")}: </strong>
														</span>
														<span>
															{utils.currencyFormatter(
																rule.officerRate,
															)}
														</span>
													</div>
													<div className="col-md-2 invoice-form-padding">
														<span className="visible-xs-* hidden-md hidden-lg">
															<strong>
																{lang.t("Set Paycode")}:{" "}
															</strong>
														</span>
														<span>{rule.paycodeId}</span>
													</div>
													<div className="col-md-2 invoice-form-padding">
														<span className="visible-xs-* hidden-md hidden-lg">
															<strong>{lang.t("Fee")}: </strong>
														</span>
														<Choose>
															<When condition={rule.fee != null}>
																<span>{rule.fee}</span>{" "}
																<span>
																	{findFeeTypeName(rule.feeType)}
																</span>
															</When>
															<Otherwise>
																<span>-</span>
															</Otherwise>
														</Choose>
													</div>
												</div>
											</div>
											<div
												style={{ display: "flex" }}
												className="col-xs-2 invoice-form-padding hidden-xs hidden-sm"
											>
												<button
													type="button"
													style={{ paddingLeft: 0 }}
													className="btn btn-link"
													onClick={() =>
														duplicateRule(rule.id, groupedRuleKey)
													}
													title={lang.t("Duplicate")}
												>
													<i className="glyphicon glyphicon-duplicate" />
												</button>
												<button
													style={{ paddingLeft: 0 }}
													type="button"
													className="btn btn-link"
													onClick={() =>
														toggleEdit(rule.id, groupedRuleKey)
													}
												>
													<i className="glyphicon glyphicon-pencil" />
												</button>
												<button
													style={{ paddingLeft: 0 }}
													type="button"
													className="btn btn-link"
													onClick={() =>
														deleteRule(rule.id, groupedRuleKey)
													}
												>
													<i className="glyphicon glyphicon-trash text-danger" />
												</button>
											</div>
										</div>
									</div>
								</If>
								<If condition={rule._editing}>
									<div
										className="row edit-section"
										key={`rule-${rule.id}`}
									>
										<div className="col-xs-12">
											<div className="row hidden-xs hidden-sm">
												<div className="col-xs-2 edit-label">
													{lang.t("Pay Code")}
												</div>
												<div className="col-xs-2 edit-label">
													{lang.t("Officer Rank")}
												</div>
												<div className="col-xs-2 edit-label">
													{lang.t("Client")}
												</div>
												<div className="col-xs-2 edit-label">
													{lang.t("Rate")}*
												</div>
												<div className="col-xs-2 edit-label">
													{lang.t("Set Paycode")}
												</div>
											</div>
											<div className="row">
												<div className="col-md-2 invoice-form-padding">
													<label
														className="visible-xs-* hidden-md hidden-lg"
														htmlFor="jobPaycodeId"
													>
														<strong>{lang.t("Pay Code")}</strong>
													</label>
													<ReactSelect
														id={`jobPaycodeId-${rule.id}`}
														name="jobPaycodeId"
														options={paycodes}
														value={rule.jobPaycodeId}
														onChange={e =>
															handleRuleChange(
																e,
																rule.id,
																groupedRuleKey,
															)
														}
													/>
												</div>
												<div className="col-md-2 invoice-form-padding">
													<label
														className="visible-xs-* hidden-md hidden-lg"
														htmlFor="officerRankId"
													>
														<strong>{lang.t("Officer Rank")}</strong>
													</label>
													<ReactSelect
														id={`rank-${rule.id}`}
														name="officerRankId"
														options={ranks}
														value={rule.officerRankId}
														onChange={e =>
															handleRuleChange(
																e,
																rule.id,
																groupedRuleKey,
															)
														}
													/>
												</div>
												<div className="col-md-2 invoice-form-padding">
													<label
														className="visible-xs-* hidden-md hidden-lg"
														htmlFor="clientId"
													>
														<strong>{lang.t("Client")}</strong>
													</label>
													<ReactSelect
														id={`client-${rule.id}`}
														name="clientId"
														options={clients}
														value={rule.clientId}
														onChange={e =>
															handleRuleChange(
																e,
																rule.id,
																groupedRuleKey,
															)
														}
													/>
												</div>
												<div className="col-md-2 invoice-form-padding">
													<label
														className="visible-xs-* hidden-md hidden-lg"
														htmlFor="officerRate"
													>
														<strong>{lang.t("Rate")}*</strong>
													</label>
													<div className="input-group">
														<div className="input-group-addon">$</div>
														<input
															name="officerRate"
															id={`officerRate-${rule.id}`}
															className="form-control invoice-modal-form"
															type="text"
															placeholder="Rate"
															value={rule.officerRate}
															onChange={e =>
																handleRuleChange(
																	e.target,
																	rule.id,
																	groupedRuleKey,
																)
															}
														/>
													</div>
												</div>
												<div className="col-md-2 invoice-form-padding">
													<label
														className="visible-xs-* hidden-md hidden-lg"
														htmlFor="paycodeId"
													>
														<strong>{lang.t("Set Paycode")}</strong>
													</label>
													<input
														name="paycodeId"
														id={`paycode-${rule.id}`}
														className="invoice-modal-form form-control"
														type="text"
														placeholder="Code"
														value={rule.paycodeId}
														onChange={e =>
															handleRuleChange(
																e.target,
																rule.id,
																groupedRuleKey,
															)
														}
													/>
												</div>
											</div>
											<div className="row">
												<If condition={rule.fee == null}>
													<div className="col-xs-4 col-md-2">
														<div className="edit-rule-buttons">
															<button
																id={`add-fee-${rule.id}`}
																className="btn primary"
																type="button"
																onClick={() =>
																	addFeeToRule(
																		rule.id,
																		groupedRuleKey,
																	)
																}
															>
																+{lang.t("Add Fee")}
															</button>
														</div>
													</div>
												</If>
												<If condition={rule.fee != null}>
													<div className="col-md-4 fee-container">
														<label htmlFor="fee">{lang.t("Fee")}</label>
														<div className="fee-rate form-group">
															<input
																id={`fee-${rule.id}`}
																name="fee"
																className="form-control fee-input"
																type="text"
																placeholder="Fee Rate"
																value={rule.fee}
																onChange={e =>
																	handleRuleChange(
																		e.target,
																		rule.id,
																		groupedRuleKey,
																	)
																}
															/>
															<select
																id={`feeType-${rule.id}`}
																className="form-control fee-select"
																name="feeType"
																value={rule.feeType}
																onChange={e =>
																	handleRuleChange(
																		e.target,
																		rule.id,
																		groupedRuleKey,
																	)
																}
															>
																<For
																	each="option"
																	of={JURISDICTION_FEE_TYPES}
																>
																	<option
																		key={`new-fee-type_${option.value}`}
																		value={option.value}
																	>
																		{option.label}
																	</option>
																</For>
															</select>
														</div>
													</div>
												</If>
												<div
													className={`col-xs-4 col-md-2 ${rule.fee != null ? "col-xs-offset-8 col-md-offset-10" : "col-xs-offset-4 col-md-offset-8"}`}
												>
													<div className="edit-rule-buttons">
														<button
															id={`cancel-rule-edit-${rule.id}`}
															className="btn danger"
															type="button"
															onClick={() =>
																cancelRuleToggleEdit(
																	rule.id,
																	groupedRuleKey,
																)
															}
														>
															{lang.t("Cancel")}
														</button>
														<button
															id={`save-rule-${rule.id}`}
															className="btn primary"
															type="button"
															disabled={
																validateUpdatingRule(
																	groupedRuleKey,
																	rule,
																) === false
															}
															onClick={() =>
																updateRule(rule.id, groupedRuleKey)
															}
														>
															{lang.t("Save")}
														</button>
													</div>
												</div>
											</div>
										</div>
									</div>
								</If>
							</For>
							<If
								condition={
									Object.keys(invoiceRules[groupedRuleKey]._newRule).length !== 0
								}
							>
								<div className="row edit-section">
									<div className="col-xs-12">
										<div
											className="row hidden-xs hidden-sm"
											style={{ paddingLeft: 0 }}
										>
											<div className="col-xs-2 edit-label">
												{lang.t("Pay Code")}
											</div>
											<div className="col-xs-2 edit-label">
												{lang.t("Officer Rank")}
											</div>
											<div className="col-xs-2 edit-label">
												{lang.t("Client")}
											</div>
											<div className="col-xs-2 edit-label">
												{lang.t("Rate")}*
											</div>
											<div className="col-xs-2 edit-label">
												{lang.t("Set Paycode")}
											</div>
										</div>
										<div className="row">
											<div className="col-md-2 invoice-form-padding">
												<label
													className="visible-xs-* hidden-md hidden-lg"
													htmlFor="jobPaycodeId"
												>
													<strong>{lang.t("Pay Code")}</strong>
												</label>
												<ReactSelect
													id={`new-rule-${invoiceRules[groupedRuleKey]}-jobPaycodeId`}
													name="jobPaycodeId"
													options={paycodes}
													value={
														invoiceRules[groupedRuleKey]._newRule
															.jobPaycodeId
													}
													onChange={e =>
														updateNewRuleFromDateRange(
															e,
															groupedRuleKey,
														)
													}
												/>
											</div>
											<div className="col-md-2 invoice-form-padding">
												<label
													className="visible-xs-* hidden-md hidden-lg"
													htmlFor="officerRankId"
												>
													<strong>{lang.t("Officer Rank")}</strong>
												</label>
												<ReactSelect
													id={`new-rule-${invoiceRules[groupedRuleKey]}-rank`}
													name="officerRankId"
													options={ranks}
													value={
														invoiceRules[groupedRuleKey]._newRule
															.officerRankId
													}
													onChange={e =>
														updateNewRuleFromDateRange(
															e,
															groupedRuleKey,
														)
													}
												/>
											</div>
											<div className="col-md-2 invoice-form-padding">
												<label
													className="visible-xs-* hidden-md hidden-lg"
													htmlFor="clientId"
												>
													<strong>{lang.t("Client")}</strong>
												</label>
												<ReactSelect
													id={`new-rule-${invoiceRules[groupedRuleKey]}-client`}
													name="clientId"
													options={clients}
													value={
														invoiceRules[groupedRuleKey]._newRule
															.clientId
													}
													onChange={e =>
														updateNewRuleFromDateRange(
															e,
															groupedRuleKey,
														)
													}
												/>
											</div>
											<div className="col-md-2 invoice-form-padding">
												<label
													className="visible-xs-* hidden-md hidden-lg"
													htmlFor="officerRate"
												>
													<strong>{lang.t("Rate")}*</strong>
												</label>
												<div className="input-group">
													<div className="input-group-addon">$</div>
													<input
														id={`new-rule-${invoiceRules[groupedRuleKey]}-officerRate`}
														name="officerRate"
														className="form-control invoice-modal-form"
														type="text"
														placeholder="Rate"
														value={
															invoiceRules[groupedRuleKey]._newRule
																.officerRate
														}
														onChange={e =>
															updateNewRuleFromDateRange(
																e.target,
																groupedRuleKey,
															)
														}
													/>
												</div>
											</div>
											<div className="col-md-2 invoice-form-padding">
												<label
													className="visible-xs-* hidden-md hidden-lg"
													htmlFor="paycodeId"
												>
													<strong>{lang.t("Set Paycode")}</strong>
												</label>
												<input
													id={`new-rule-${invoiceRules[groupedRuleKey]}-paycodeId`}
													name="paycodeId"
													className="invoice-modal-form form-control"
													type="text"
													placeholder="Code"
													value={
														invoiceRules[groupedRuleKey]._newRule
															.paycodeId
													}
													onChange={e =>
														updateNewRuleFromDateRange(
															e.target,
															groupedRuleKey,
														)
													}
												/>
											</div>
										</div>
										<div className="row">
											<If
												condition={
													!invoiceRules[groupedRuleKey]._newRule._showFee
												}
											>
												<div className="col-xs-4 col-md-2">
													<div className="edit-rule-buttons">
														<button
															id={`add-fee-${invoiceRules[groupedRuleKey]}`}
															className="btn primary"
															type="button"
															onClick={() =>
																addFeeToNewRuleFromDateRange(
																	groupedRuleKey,
																)
															}
														>
															+{lang.t("Add Fee")}
														</button>
													</div>
												</div>
											</If>
											<If
												condition={
													invoiceRules[groupedRuleKey]._newRule._showFee
												}
											>
												<div className="col-md-4 fee-container">
													<label htmlFor="fee">{lang.t("Fee")}</label>
													<div className="fee-rate form-group">
														<input
															id={`fee-${invoiceRules[groupedRuleKey]}`}
															name="fee"
															className="form-control fee-input"
															type="text"
															placeholder="Fee Rate"
															value={
																invoiceRules[groupedRuleKey]
																	._newRule.fee
															}
															onChange={e =>
																updateNewRuleFromDateRange(
																	e.target,
																	groupedRuleKey,
																)
															}
														/>
														<select
															id={`feeType-${invoiceRules[groupedRuleKey]}`}
															className="form-control fee-select"
															name="feeType"
															value={
																invoiceRules[groupedRuleKey]
																	._newRule.feeType
															}
															onChange={e =>
																updateNewRuleFromDateRange(
																	e.target,
																	groupedRuleKey,
																)
															}
														>
															<For
																each="option"
																of={JURISDICTION_FEE_TYPES}
															>
																<option
																	key={`new-fee-type_${option.value}`}
																	value={option.value}
																>
																	{option.label}
																</option>
															</For>
														</select>
													</div>
												</div>
											</If>
											<div
												className={`col-xs-4 col-md-2 ${
													invoiceRules[groupedRuleKey]._newRule._showFee
														? "col-xs-offset-8 col-md-offset-10"
														: "col-xs-offset-4 col-md-offset-8"
												}`}
											>
												<div className="edit-rule-buttons">
													<button
														id={`cancel-new-rule-${invoiceRules[groupedRuleKey]}`}
														className="btn danger"
														type="button"
														onClick={() =>
															cancelNewRuleFromDateRange(
																groupedRuleKey,
															)
														}
													>
														{lang.t("Cancel")}
													</button>
													<button
														id={`save-new-rule-${invoiceRules[groupedRuleKey]}`}
														className="btn primary"
														type="button"
														disabled={
															!validateUpdatingRule(
																groupedRuleKey,
																invoiceRules[groupedRuleKey]
																	._newRule,
															)
														}
														onClick={() =>
															saveNewRuleFromDateRange(groupedRuleKey)
														}
													>
														{lang.t("Save")}
													</button>
												</div>
											</div>
										</div>
									</div>
								</div>
							</If>
						</If>
					</div>
				</For>
			</If>
			<Choose>
				<When condition={newRule !== null}>
					<div className="edit-section new-rule-section">
						<div className="row">
							<Choose>
								<When condition={baseRatesRules}>
									<div className="col-xs-5 col-md-3">{lang.t("Start Date")}*</div>
									<div className="col-xs-5 col-md-3">{lang.t("End Date")}*</div>
								</When>
								<Otherwise>
									<div className="col-xs-5 col-md-3">{lang.t("Dates")}*</div>
								</Otherwise>
							</Choose>
						</div>
						<div
							style={{ paddingBottom: 15 }}
							className="row"
						>
							<Choose>
								<When condition={baseRatesRules}>
									<div className="col-xs-5 col-md-3 form-group">
										<input
											name="effectiveDateStart"
											id="new-rule-effectiveDateStart"
											className="form-control"
											type="date"
											value={newRule.effectiveDateStart}
											onChange={e => updateNewRule(e.target)}
										/>
									</div>
									<div className="col-xs-5 col-md-3 form-group">
										<input
											name="effectiveDateEnd"
											id="new-rule-effectiveDateEnd"
											className="form-control"
											type="date"
											min={newRule.effectiveDateStart}
											value={newRule.effectiveDateEnd}
											onChange={e => updateNewRule(e.target)}
										/>
									</div>
								</When>
								<Otherwise>
									<Choose>
										<When condition={showDatePicker}>
											<div className="col-xs-5 col-md-3 form-group">
												<input
													name="effectiveDateStart"
													id="new-rule-effectiveDateStart"
													className="form-control"
													type="date"
													value={holidayDateInput}
													onChange={e =>
														setHolidayDateInput(e.target.value)
													}
												/>
											</div>
											<div className="col-xs-3 col-sm-1">
												<div
													className="edit-rule-buttons"
													style={{ marginTop: 0 }}
												>
													<button
														id="cancel-add-specific-date"
														className="btn danger"
														type="button"
														onClick={() => cancelAddHolidayRateInput()}
													>
														{lang.t("Cancel")}
													</button>
													<button
														type="button"
														id="add-specific-date"
														aria-label="add specific date"
														onClick={() => handleAddCustomDate()}
														className="btn btn-link"
													>
														+ {lang.t("Add")}
													</button>
												</div>
											</div>
										</When>
										<Otherwise>
											<button
												type="button"
												id="add-specific-date"
												aria-label="add specific date"
												onClick={handleAddHolidayRate}
												className="btn btn-link"
											>
												+ {lang.t("Add Holiday")}
											</button>
										</Otherwise>
									</Choose>
									<div className="col-xs-12">
										<ul className="list-unstyled custom-dates">
											<If condition={holidays?.length > 0}>
												<For
													each="date"
													of={holidays}
												>
													<li
														key={`custom-date-${date.replace(/\//g, "-")}`}
													>
														<button
															type="button"
															data-cd={date}
															className="btn btn-rounded"
															onClick={() => deleteCustomDate(date)}
														>
															{utils.formatDate(
																`${utils.fixIELocaleString(date)}T00:00:00`,
																"en-US",
																undefined,
																"2-digit",
															)}
															<span className="badge">&#10005;</span>
														</button>
													</li>
												</For>
											</If>
										</ul>
									</div>
								</Otherwise>
							</Choose>
						</div>
						<div className="row hidden-xs hidden-sm">
							<div className="col-md-2 new-edit-label">{lang.t("Pay Code")}</div>
							<div className="col-md-2 new-edit-label">{lang.t("Officer Rank")}</div>
							<div className="col-md-2 new-edit-label">{lang.t("Client")}</div>
							<div className="col-md-2 new-edit-label">{lang.t("Rate")}*</div>
							<div className="col-md-2 new-edit-label">{lang.t("Set Paycode")}</div>
						</div>
						<div className="row">
							<div className="col-md-2 new-rule-range-form-padding">
								<label
									className="visible-xs-* hidden-md hidden-lg"
									htmlFor="jobPaycodeId"
								>
									<strong>{lang.t("Pay Code")}</strong>
								</label>
								<ReactSelect
									id="new-rule-jobPaycodeId"
									name="jobPaycodeId"
									options={paycodes}
									value={newRule.jobPaycodeId}
									onChange={e => updateNewRule(e)}
								/>
							</div>
							<div className="col-md-2 new-rule-range-form-padding">
								<label
									className="visible-xs-* hidden-md hidden-lg"
									htmlFor="officerRankId"
								>
									<strong>{lang.t("Officer Rank")}</strong>
								</label>
								<ReactSelect
									id="new-rule-rank"
									name="officerRankId"
									options={ranks}
									value={newRule.officerRankId}
									onChange={e => updateNewRule(e)}
								/>
							</div>
							<div className="col-md-2 new-rule-range-form-padding">
								<label
									className="visible-xs-* hidden-md hidden-lg"
									htmlFor="clientId"
								>
									<strong>{lang.t("Client")}</strong>
								</label>
								<ReactSelect
									id="new-rule-client"
									name="clientId"
									options={clients}
									value={newRule.clientId}
									onChange={e => updateNewRule(e)}
								/>
							</div>
							<div className="col-md-2 new-rule-range-form-padding">
								<label
									className="visible-xs-* hidden-md hidden-lg"
									htmlFor="officerRate"
								>
									<strong>{lang.t("Rate")}*</strong>
								</label>
								<div className="input-group">
									<div className="input-group-addon">$</div>
									<input
										id="new-rule-officerRate"
										name="officerRate"
										className="form-control invoice-modal-form"
										type="text"
										placeholder="Rate"
										value={newRule.officerRate}
										onChange={e => updateNewRule(e.target)}
									/>
								</div>
							</div>
							<div className="col-md-2 new-rule-range-form-padding">
								<label
									className="visible-xs-* hidden-md hidden-lg"
									htmlFor="paycodeId"
								>
									<strong>{lang.t("Set Paycode")}</strong>
								</label>
								<input
									id="new-rule-paycodeId"
									name="paycodeId"
									className="invoice-modal-form form-control"
									type="text"
									placeholder="Code"
									value={newRule.paycodeId}
									onChange={e => updateNewRule(e.target)}
								/>
							</div>
						</div>
						<div className="row">
							<If condition={!newRule._showFee}>
								<div className="col-xs-4 col-md-2">
									<div className="edit-rule-buttons">
										<button
											id="add-new-rule-fee"
											className="btn primary"
											type="button"
											onClick={() => addFeeToNewRule()}
										>
											+{lang.t("Add Fee")}
										</button>
									</div>
								</div>
							</If>
							<If condition={newRule._showFee}>
								<div className="col-md-4 fee-container new-fee">
									<label htmlFor="fee">{lang.t("Fee")}</label>
									<div className="fee-rate form-group">
										<input
											id="new-rule-fee"
											name="fee"
											className="form-control fee-input"
											type="text"
											placeholder="Fee Rate"
											value={newRule.fee}
											onChange={e => updateNewRule(e.target)}
										/>
										<select
											className="form-control fee-select"
											name="feeType"
											value={newRule.feeType}
											onChange={e => updateNewRule(e.target)}
										>
											<For
												each="option"
												of={JURISDICTION_FEE_TYPES}
											>
												<option
													key={`new-fee-type_${option.value}`}
													value={option.value}
												>
													{option.label}
												</option>
											</For>
										</select>
									</div>
								</div>
							</If>
							<div
								className={`col-xs-4 col-md-2 ${newRule._showFee ? "col-xs-offset-8 col-md-offset-10" : "col-xs-offset-4 col-md-offset-8"}`}
							>
								<div className="edit-rule-buttons">
									<button
										id="cancel-new-rule"
										className="btn danger"
										type="button"
										onClick={() => cancelNewRule()}
									>
										{lang.t("Cancel")}
									</button>
									<button
										id="save-new-rule"
										className="btn primary"
										type="button"
										disabled={!validateRate(newRule)}
										onClick={() => createNewRule()}
									>
										{lang.t("Save")}
									</button>
								</div>
							</div>
						</div>
					</div>
				</When>
				<Otherwise>
					<div>
						<button
							type="button"
							className="btn btn-link"
							title={lang.t("Add rate")}
							onClick={() => addNewRule()}
						>
							<span>{`+ ${lang.t("Create New Rule with Date Range")}`}</span>
						</button>
					</div>
				</Otherwise>
			</Choose>
		</div>
	);
};

RatesRules.propTypes = {
	invoiceRules: PropTypes.object,
	clients: PropTypes.array,
	ranks: PropTypes.array,
	paycodes: PropTypes.array,
	userData: PropTypes.object,
	onCreate: PropTypes.func.isRequired,
	onUpdate: PropTypes.func.isRequired,
	onDelete: PropTypes.func.isRequired,
	onRuleBulkUpdate: PropTypes.func.isRequired,
	onBulkCreate: PropTypes.func.isRequired,
	baseRatesRules: PropTypes.bool,
	onSetInvoicingRule: PropTypes.func.isRequired,
};

RatesRules.defaultProps = {
	invoiceRules: [],
	clients: [],
	ranks: [],
	paycodes: [],
	userData: {},
	baseRatesRules: true,
};

export default RatesRules;
